import { isEqual, omit } from 'lodash';
import type { RxCollection } from 'rxdb';

import { extractBlockIds } from '@/utils/editor-utils';

import type { Block, Note } from '@flintmint/core-shared/rxdb';

export const cleanupTemplateReferencesOnDelete = async (notesCollection: RxCollection<Note>, deletedNote: Note): Promise<any> => {
  // If a template is deleted, clean up all notes that reference it
  if (deletedNote.variant === 'templates') {
    return clearTemplateIdFromLinkedNotes(notesCollection, deletedNote.id);
  }
};

export const clearTemplateIdFromLinkedNotes = async (notesCollection: RxCollection<Note>, templateId: string): Promise<any> => {
  const linkedNotes = await notesCollection
    .find({
      selector: {
        templateId,
      },
    })
    .exec();

  return Promise.all(linkedNotes.map((note) => note.incrementalPatch({ templateId: null })));
};

// Delete-time instruction cleanup (cleanupDeletedInstructionReferences) relies on blocks being patched before note writes, because this re-sync re-adds ids from still-dirty blocks.
export const syncNoteBlockReferences = async (blocksCollection: RxCollection<Block>, note: Note) => {
  const blockIds = extractBlockIds(note.content);

  // Reconcile against the persisted `blockIds` rather than the previous content's blocks. A note
  // created with content that already holds blocks starts with an empty `blockIds`, and comparing
  // content to content would never repair it: the block set is unchanged on every later save.
  if (!isEqual([...blockIds].sort(), [...(note.blockIds ?? [])].sort())) {
    note.blockIds = blockIds;
  }

  const blocks = await blocksCollection.find({ selector: { id: { $in: blockIds } } }).exec();

  note.blockInstructions = buildNoteBlockInstructions(blocks, note);
};

const buildNoteBlockInstructions = (blocks: Block[], note: Note): Record<string, Record<string, boolean>> => {
  if (!blocks.length) {
    return {};
  }

  const latestInstructionsByBlockId = Object.fromEntries(blocks.map((block) => [block.id, block.instructions ?? {}]));

  const existing = note.blockInstructions ?? {};
  const validBlockIds = new Set(blocks.map((b) => b.id));

  // Keep instructions only for blocks still present in the note
  const result: Record<string, Record<string, boolean>> = Object.fromEntries(
    Object.entries(existing)
      .filter(([blockId]) => validBlockIds.has(blockId))
      .map(([blockId, instructions]) => [blockId, { ...instructions }])
  );

  // Merge each block’s instructions
  for (const [blockId, latestInstructions] of Object.entries(latestInstructionsByBlockId)) {
    const existingForBlock = result[blockId] ?? {};
    const latestIds = new Set(Object.keys(latestInstructions));

    // Remove outdated instructions
    for (const existingId of Object.keys(existingForBlock)) {
      if (!latestIds.has(existingId)) {
        delete existingForBlock[existingId];
      }
    }

    // Add new instructions or preserve user overrides
    for (const [id, defaultValue] of Object.entries(latestInstructions)) {
      if (existingForBlock[id] === undefined) {
        existingForBlock[id] = defaultValue;
      }
    }

    result[blockId] = existingForBlock;
  }

  return result;
};

export const updateNotesReferencingBlock = async (notesCollection: RxCollection<Note>, block: Block): Promise<any> => {
  const notesWithBlock = await notesCollection
    .find({
      selector: {
        blockIds: { $in: [block.id] },
      },
    })
    .exec();

  const updatePromises = notesWithBlock.map(async (noteDoc) => {
    const note = noteDoc.toJSON() as Note;

    const instructions = buildNoteBlockInstructions([block], note);

    // Update the actual note with the result
    return noteDoc.incrementalPatch({
      blockInstructions: {
        ...note.blockInstructions,
        ...instructions,
      },
    });
  });

  await Promise.all(updatePromises);
};

export const removeReferenceFromNotes = async (
  notesCollection: RxCollection<Note>,
  docId: string,
  fieldName: 'userSources'
): Promise<void> => {
  try {
    const notesWithReference = await notesCollection
      .find({
        selector: {
          [`${fieldName}.${docId}`]: { $exists: true },
        },
      })
      .exec();

    const updatePromises = notesWithReference.map(async (noteDoc) => {
      const note = noteDoc.toJSON();
      const updatedField = { ...note[fieldName] };
      delete updatedField[docId];

      return noteDoc.incrementalPatch({
        [fieldName]: updatedField,
      });
    });

    const results = await Promise.allSettled(updatePromises);
    logPatchRejections(results, notesWithReference, `while removing ${fieldName} ${docId} from notes`);
  } catch (error) {
    console.error(`Error removing ${fieldName} ${docId} from note`, error);
  }
};

// Delete-time cleanup for an instruction: gather docs that still reference the id, then patch.
// note.blockInstructions is keyed by blockId, so we find blocks that hold the instruction and
// look up notes in one combined query with `blockInstructions.<blockId>.<instructionId>` arms —
// works even when `blockIds` is missing on older notes. Blocks are patched before notes because
// every note write re-syncs blockInstructions from the blocks collection (preSaveNote), which
// would re-add the id if a block still held it.
export const cleanupDeletedInstructionReferences = async (
  notesCollection: RxCollection<Note>,
  blocksCollection: RxCollection<Block>,
  instructionId: string
): Promise<void> => {
  try {
    const blocksWithInstruction = await blocksCollection
      .find({
        selector: {
          [`instructions.${instructionId}`]: { $exists: true },
        },
      })
      .exec();

    const blockResults = await Promise.allSettled(
      blocksWithInstruction.map(async (blockDoc) =>
        blockDoc.incrementalModify((data) => ({ ...data, instructions: omit(data.instructions, instructionId) }))
      )
    );
    logPatchRejections(blockResults, blocksWithInstruction, `while cleaning up deleted instruction ${instructionId}`);

    const blockIds = blocksWithInstruction.map((block) => block.id);
    const notesToPatch = await notesCollection
      .find({
        selector: {
          $or: [
            { [`instructions.${instructionId}`]: { $exists: true } },
            ...blockIds.map((blockId) => ({ [`blockInstructions.${blockId}.${instructionId}`]: { $exists: true } })),
          ],
        },
      })
      .exec();

    const noteResults = await Promise.allSettled(
      notesToPatch.map(async (noteDoc) => {
        const note = noteDoc.toJSON();
        const patch: Partial<Note> = {};

        if (note.instructions && instructionId in note.instructions) {
          patch.instructions = omit(note.instructions, instructionId);
        }

        // The computed blockInstructions is discarded on write (preSaveNote re-syncs it from blocks); this loop exists to detect staleness so a save fires for notes matched only via the nested $or arm.
        const existingBlockInstructions = note.blockInstructions ?? {};
        let blockInstructionsChanged = false;
        const nextBlockInstructions: Record<string, Record<string, boolean>> = { ...existingBlockInstructions };

        for (const [blockId, instructionsMap] of Object.entries(existingBlockInstructions)) {
          if (!instructionsMap || !(instructionId in instructionsMap)) continue;

          const nextInstructionsMap = omit(instructionsMap, instructionId);

          if (Object.keys(nextInstructionsMap).length === 0) {
            delete nextBlockInstructions[blockId];
          } else {
            nextBlockInstructions[blockId] = nextInstructionsMap;
          }

          blockInstructionsChanged = true;
        }

        if (blockInstructionsChanged) {
          patch.blockInstructions = nextBlockInstructions;
        }

        if (Object.keys(patch).length === 0) return;
        return noteDoc.incrementalPatch(patch);
      })
    );
    logPatchRejections(noteResults, notesToPatch, `while cleaning up deleted instruction ${instructionId}`);
  } catch (error) {
    console.error(`Error cleaning up deleted instruction ${instructionId} references`, error);
  }
};

// console.error is the only path these failures reach Sentry (captureConsoleIntegration).
const logPatchRejections = (results: PromiseSettledResult<unknown>[], docs: { id: string }[], context: string) => {
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error(`Error patching ${docs[index].id} ${context}`, result.reason);
    }
  });
};
